(() => {
  if (window.__svWishlistLoadedV2) return;
  window.__svWishlistLoadedV2 = true;

  const config = window.SVWishlistConfig || {};
  const storageKey = 'svWishlistLocal:v1';
  const wishlistUrl = config.wishlistUrl || '/pages/wishlist';
  const shopUrl = config.shopUrl || '/collections/all';
  const defaultCurrency = config.currency || 'INR';

  const state = {
    items: [],
    ready: false,
  };

  let toastTimer = 0;
  let uiSyncFrame = 0;
  let uiSyncTimer = 0;
  let observerFrame = 0;
  const pendingObserverRoots = new Set();

  function normalizeId(value) {
    const match = String(value || '').match(/(\d+)$/);
    return match ? match[1] : '';
  }

  function productKey(item) {
    return normalizeId(item?.productId);
  }

  function sanitizeItem(item) {
    const productId = normalizeId(item?.productId);
    const variantId = normalizeId(item?.variantId);
    if (!productId || !variantId) return null;

    return {
      productId,
      variantId,
      productHandle: String(item?.productHandle || ''),
      productTitle: String(item?.productTitle || 'Product'),
      variantTitle: String(item?.variantTitle || ''),
      productUrl: String(item?.productUrl || `/products/${item?.productHandle || ''}`),
      imageUrl: String(item?.imageUrl || ''),
      priceCents: Math.max(0, Number(item?.priceCents || 0)),
      currency: String(item?.currency || defaultCurrency),
      createdAt: item?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  }

  function readItems() {
    try {
      const parsed = JSON.parse(localStorage.getItem(storageKey) || '[]');
      if (!Array.isArray(parsed)) return [];
      return parsed.map(sanitizeItem).filter(Boolean).slice(0, 100);
    } catch (_) {
      return [];
    }
  }

  function saveItems(items) {
    try {
      localStorage.setItem(storageKey, JSON.stringify(items.slice(0, 100)));
    } catch (error) {
      console.warn('[SV Wishlist] Unable to save wishlist in this browser.', error);
    }
  }

  function money(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: currency || defaultCurrency,
        maximumFractionDigits: Number(cents) % 100 === 0 ? 0 : 2,
      }).format(Number(cents || 0) / 100);
    } catch (_) {
      return `${(Number(cents || 0) / 100).toFixed(2)} ${currency || ''}`.trim();
    }
  }

  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function showToast(message) {
    let toast = document.querySelector('[data-sv-wishlist-toast]');

    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'sv-wishlist-toast';
      toast.dataset.svWishlistToast = '';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      toast.setAttribute('aria-atomic', 'true');
      document.body.appendChild(toast);
    }

    window.clearTimeout(toastTimer);
    toast.textContent = message;
    toast.classList.add('is-visible');
    toastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 2600);
  }

  function hasProduct(productId) {
    const key = normalizeId(productId);
    return state.items.some((item) => productKey(item) === key);
  }

  function getItem(productId) {
    const key = normalizeId(productId);
    return state.items.find((item) => productKey(item) === key) || null;
  }

  function scheduleUiSync() {
    if (uiSyncFrame || uiSyncTimer) return;

    uiSyncFrame = requestAnimationFrame(() => {
      uiSyncFrame = 0;

      // Let the interaction paint first. Coalesce clicks in the same frame
      // and while the pending refresh is queued to avoid duplicate DOM work.
      uiSyncTimer = window.setTimeout(() => {
        uiSyncTimer = 0;
        renderButtons();
        updateHeaderCount();
        renderWishlistPage();
      }, 0);
    });
  }

  function setItems(items) {
    const seen = new Set();

    state.items = (Array.isArray(items) ? items : [])
      .map(sanitizeItem)
      .filter((item) => {
        if (!item || seen.has(item.productId)) return false;
        seen.add(item.productId);
        return true;
      });

    saveItems(state.items);
    scheduleUiSync();
  }

  function getButtonItem(button) {
    const productId = normalizeId(button.dataset.productId);
    const productRoot = button.closest(
      'product-information, .product-information, product-form-component, product-card, .shopify-section'
    );
    const variantInput = productRoot?.querySelector('form[action*="/cart/add"] [name="id"]');
    const variantId = normalizeId(variantInput?.value || button.dataset.variantId);

    if (variantId) button.dataset.variantId = variantId;

    return sanitizeItem({
      productId,
      variantId,
      productHandle: button.dataset.productHandle,
      productTitle: button.dataset.productTitle,
      variantTitle: button.dataset.variantTitle,
      productUrl: button.dataset.productUrl,
      imageUrl: button.dataset.productImage,
      priceCents: button.dataset.priceCents,
      currency: button.dataset.currency,
    });
  }

  function setButtonState(button) {
    const active = hasProduct(button.dataset.productId);
    const title = button.dataset.productTitle || 'product';
    const label = button.querySelector('[data-sv-wishlist-label]');

    button.setAttribute('aria-pressed', String(active));
    button.setAttribute(
      'aria-label',
      `${active ? 'Remove' : 'Add'} ${title} ${active ? 'from' : 'to'} wishlist`
    );

    if (label) label.textContent = active ? 'Saved to wishlist' : 'Add to wishlist';
  }

  function renderButtons(root = document) {
    root.querySelectorAll?.('[data-sv-wishlist-button]').forEach(setButtonState);
  }

  function ensureHeaderLink() {
    const actions = document.querySelector('header-actions');
    if (!actions || actions.querySelector('[data-sv-wishlist-header]')) return;

    const link = document.createElement('a');
    link.href = wishlistUrl;
    link.className = 'header-actions__action button-unstyled sv-wishlist-header-link';
    link.dataset.svWishlistHeader = '';
    link.setAttribute('aria-label', 'Wishlist');
    link.innerHTML = `
      <svg aria-hidden="true" viewBox="0 0 24 24" width="20" height="20" fill="none">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78Z" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <span class="sv-wishlist-header-count" data-sv-wishlist-count hidden>0</span>
    `;

    const cartAction = actions.querySelector(
      '[aria-controls="sv-cart-drawer"], .action__cart, [data-testid="cart-drawer-trigger"]'
    );
    actions.insertBefore(link, cartAction || null);
  }

  function updateHeaderCount() {
    ensureHeaderLink();
    document.querySelectorAll('[data-sv-wishlist-count]').forEach((node) => {
      node.textContent = String(state.items.length);
      node.hidden = state.items.length === 0;
    });
  }

  function animateWishlistAddition(button) {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const targets = new Set([
      button,
      ...document.querySelectorAll('[data-sv-wishlist-header]'),
    ]);

    targets.forEach((target) => {
      if (!(target instanceof HTMLElement)) return;

      target.classList.remove('is-vibrating');

      // Restart after the current interaction paint instead of forcing a
      // synchronous layout read with offsetWidth.
      requestAnimationFrame(() => {
        if (!target.isConnected) return;
        target.classList.add('is-vibrating');

        window.setTimeout(() => {
          target.classList.remove('is-vibrating');
        }, 760);
      });
    });

    if (
      !reducedMotion &&
      'vibrate' in navigator &&
      window.matchMedia('(pointer: coarse)').matches
    ) {
      try {
        navigator.vibrate([24, 18, 34]);
      } catch (_) {}
    }
  }

  function toggle(button) {
    if (button.classList.contains('is-loading')) return;

    const item = getButtonItem(button);
    if (!item) {
      showToast('Please select an available product option.');
      return;
    }

    button.classList.add('is-loading');
    button.disabled = true;

    let addedToWishlist = false;

    try {
      const existing = hasProduct(item.productId);
      const next = existing
        ? state.items.filter((entry) => productKey(entry) !== item.productId)
        : [item, ...state.items.filter((entry) => productKey(entry) !== item.productId)];

      setItems(next);
      addedToWishlist = !existing;
      showToast(existing ? 'Removed from wishlist.' : 'Added to your wishlist.');
    } finally {
      button.classList.remove('is-loading');
      button.disabled = false;
      setButtonState(button);

      if (addedToWishlist) {
        animateWishlistAddition(button);
      }
    }
  }

  function wishlistCardHtml(item) {
    const image = item.imageUrl
      ? `<img src="${escapeHtml(item.imageUrl)}" alt="${escapeHtml(item.productTitle)}" width="720" height="900" loading="lazy" decoding="async">`
      : `<span class="sv-wishlist-card__placeholder" aria-hidden="true">
          <svg viewBox="0 0 48 48" width="38" height="38" fill="none">
            <path d="M9 35.5 19.5 24l7 7 5.5-6 7 10.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <circle cx="17" cy="16" r="3" stroke="currentColor" stroke-width="1.5"/>
            <rect x="6.5" y="7.5" width="35" height="33" rx="5" stroke="currentColor" stroke-width="1.5"/>
          </svg>
        </span>`;

    return `<article class="sv-wishlist-card" data-sv-wishlist-card="${escapeHtml(item.productId)}">
      <div class="sv-wishlist-card__media">
        <a class="sv-wishlist-card__image" href="${escapeHtml(item.productUrl)}" aria-label="View ${escapeHtml(item.productTitle)}">${image}</a>
        <button
          class="sv-wishlist-card__remove"
          type="button"
          data-sv-wishlist-remove="${escapeHtml(item.productId)}"
          aria-label="Remove ${escapeHtml(item.productTitle)} from wishlist"
          title="Remove from wishlist"
        >
          <svg aria-hidden="true" viewBox="0 0 24 24" width="17" height="17" fill="none">
            <path d="m7 7 10 10M17 7 7 17" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/>
          </svg>
        </button>
      </div>

      <div class="sv-wishlist-card__body">
        <div class="sv-wishlist-card__details">
          <a class="sv-wishlist-card__title" href="${escapeHtml(item.productUrl)}">${escapeHtml(item.productTitle)}</a>
          ${item.variantTitle ? `<p class="sv-wishlist-card__variant">${escapeHtml(item.variantTitle)}</p>` : ''}
          <p class="sv-wishlist-card__price">${escapeHtml(money(item.priceCents, item.currency))}</p>
        </div>

        <button
          class="sv-wishlist-card__add"
          type="button"
          data-sv-wishlist-add="${escapeHtml(item.productId)}"
        >
          <svg aria-hidden="true" viewBox="0 0 24 24" width="16" height="16" fill="none">
            <path d="M3.5 5h2l1.8 9.15a2 2 0 0 0 1.96 1.62h7.48a2 2 0 0 0 1.95-1.56L20.25 8H6.1" stroke="currentColor" stroke-width="1.65" stroke-linecap="round" stroke-linejoin="round"/>
            <circle cx="9.5" cy="19.5" r="1.15" fill="currentColor"/>
            <circle cx="17" cy="19.5" r="1.15" fill="currentColor"/>
          </svg>
          <span data-sv-wishlist-add-label>Add to bag</span>
        </button>
      </div>
    </article>`;
  }

  function renderWishlistPage() {
    const page = document.querySelector('[data-sv-wishlist-page]');
    if (!page) return;

    const grid = page.querySelector('[data-sv-wishlist-grid]');
    const status = page.querySelector('[data-sv-wishlist-status]');
    if (!grid) return;

    if (!state.ready) {
      if (status) status.textContent = 'Loading your wishlist…';
      return;
    }

    if (status) {
      status.textContent = state.items.length
        ? `${state.items.length} ${state.items.length === 1 ? 'piece' : 'pieces'} saved`
        : '';
    }

    if (!state.items.length) {
      grid.innerHTML = `<div class="sv-wishlist-empty">
        <div class="sv-wishlist-empty__inner">
          <span class="sv-wishlist-empty__icon" aria-hidden="true">
            <svg viewBox="0 0 48 48" width="42" height="42" fill="none">
              <path d="M40.2 10.8a10 10 0 0 0-14.14 0L24 12.86l-2.06-2.06A10 10 0 1 0 7.8 24.94L24 41.14l16.2-16.2a10 10 0 0 0 0-14.14Z" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
          <p class="sv-wishlist-empty__eyebrow">Your saved edit</p>
          <h2>Your wishlist is waiting</h2>
          <p>Tap the heart on any piece you love and it will appear here.</p>
          <a href="${escapeHtml(shopUrl)}">Explore the collection</a>
        </div>
      </div>`;
      return;
    }

    grid.innerHTML = state.items.map(wishlistCardHtml).join('');
  }

  function removeFromPage(productId) {
    const item = getItem(productId);
    if (!item) return;

    setItems(state.items.filter((entry) => productKey(entry) !== item.productId));
    showToast('Removed from wishlist.');
  }

  async function addToCart(productId, button) {
    const item = getItem(productId);

    if (!item || !item.variantId) {
      showToast('This product option is no longer available.');
      return;
    }

    if (button.dataset.svAdding === 'true') return;

    const label = button.querySelector('[data-sv-wishlist-add-label]');
    const previousLabel = label?.textContent || 'Add to bag';
    const root = window.Shopify?.routes?.root || '/';
    const addUrl = `${root}cart/add.js`;
    const cartUrl = `${root}cart.js`;

    button.dataset.svAdding = 'true';
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.classList.remove('is-added');
    if (label) label.textContent = 'Adding…';

    try {
      const formData = new FormData();
      formData.append('id', item.variantId);
      formData.append('quantity', '1');

      const response = await fetch(addUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      });

      const addedItem = await response.json().catch(() => null);

      if (!response.ok || addedItem?.status) {
        throw new Error(
          addedItem?.description ||
          addedItem?.message ||
          'Unable to add this product to cart.'
        );
      }

      const cartResponse = await fetch(cartUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });

      if (!cartResponse.ok) {
        throw new Error('The product was added, but the cart could not refresh.');
      }

      const cart = await cartResponse.json();

      button.classList.add('is-added');
      if (label) label.textContent = 'Added ✓';

      const eventDetail = {
        resource: cart,
        sourceId: 'sv-wishlist-page',
        data: {
          source: 'product-form-component',
          didError: false,
          action: 'add',
          item: addedItem,
          itemCount: 1,
          addedQuantity: 1,
          productId: item.productId,
          variantId: item.variantId,
          productTitle: item.productTitle,
        },
      };

      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: eventDetail,
      }));

      document.dispatchEvent(new CustomEvent('shopify:cart:lines-update', {
        bubbles: true,
        detail: {
          action: 'add',
          context: 'wishlist',
          resource: addedItem,
        },
      }));

      if (
        document.visibilityState === 'visible' &&
        typeof navigator.vibrate === 'function' &&
        window.matchMedia('(pointer: coarse)').matches
      ) {
        try {
          navigator.vibrate(12);
        } catch (_) {}
      }

      window.setTimeout(() => {
        if (!button.isConnected) return;
        button.disabled = false;
        button.dataset.svAdding = 'false';
        button.removeAttribute('aria-busy');
        button.classList.remove('is-added');
        if (label) label.textContent = previousLabel;
      }, 900);
    } catch (error) {
      button.disabled = false;
      button.dataset.svAdding = 'false';
      button.removeAttribute('aria-busy');
      button.classList.remove('is-added');
      if (label) label.textContent = previousLabel;
      showToast(error?.message || 'Unable to add this product to cart.');
    }
  }

  document.addEventListener('click' , (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const wishlistButton = target.closest('[data-sv-wishlist-button]');
    if (wishlistButton instanceof HTMLButtonElement) {
      event.preventDefault();
      event.stopPropagation();
      toggle(wishlistButton);
      return;
    }

    const remove = target.closest('[data-sv-wishlist-remove]');
    if (remove instanceof HTMLButtonElement) {
      event.preventDefault();
      removeFromPage(remove.dataset.svWishlistRemove);
      return;
    }

    const add = target.closest('[data-sv-wishlist-add]');
    if (add instanceof HTMLButtonElement) {
      event.preventDefault();
      addToCart(add.dataset.svWishlistAdd, add);
    }
  }, true);

  document.addEventListener('variant:update', (event) => {
    const detail = event?.detail || {};
    const variant = detail.resource || {};
    const productId = normalizeId(detail?.data?.productId || detail?.data?.product?.id);
    const variantId = normalizeId(variant.id);
    if (!productId || !variantId) return;

    document
      .querySelectorAll(`[data-sv-wishlist-button][data-product-id="${productId}"]`)
      .forEach((button) => {
        button.dataset.variantId = variantId;
        button.dataset.variantTitle = variant.title === 'Default Title' ? '' : String(variant.title || '');
        if (variant.price != null) button.dataset.priceCents = String(variant.price);
        const image = variant.featured_media?.preview_image?.src || variant.featured_image?.src;
        if (image) button.dataset.productImage = image;
      });
  });

  window.addEventListener('storage', (event) => {
    if (event.key !== storageKey) return;
    state.items = readItems();
    scheduleUiSync();
  });

  function queueObserverRoot(node) {
    if (!(node instanceof Element)) return;

    if (node.matches('[data-sv-wishlist-button], [data-sv-wishlist-count], [data-sv-wishlist-header], header-actions')) {
      pendingObserverRoots.add(node);
      return;
    }

    if (node.querySelector?.('[data-sv-wishlist-button], [data-sv-wishlist-count], [data-sv-wishlist-header], header-actions')) {
      pendingObserverRoots.add(node);
    }
  }

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) queueObserverRoot(node);
    }

    if (pendingObserverRoots.size === 0 || observerFrame) return;

    observerFrame = requestAnimationFrame(() => {
      observerFrame = 0;
      const roots = Array.from(pendingObserverRoots);
      pendingObserverRoots.clear();

      let headerMayHaveChanged = false;
      for (const root of roots) {
        if (!(root instanceof Element) || !root.isConnected) continue;

        if (
          root.matches('header-actions, [data-sv-wishlist-count], [data-sv-wishlist-header]') ||
          root.querySelector?.('header-actions, [data-sv-wishlist-count], [data-sv-wishlist-header]')
        ) {
          headerMayHaveChanged = true;
        }

        if (root.matches('[data-sv-wishlist-button]')) {
          setButtonState(root);
        }
        renderButtons(root);
      }

      if (headerMayHaveChanged) {
        ensureHeaderLink();
        updateHeaderCount();
      }
    });
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });

  function initialise() {
    state.items = readItems();
    state.ready = true;
    renderButtons();
    updateHeaderCount();
    renderWishlistPage();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, { once: true });
  } else {
    initialise();
  }
})();